import { logger } from '../../utils/logger';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

export interface RetryImportResult {
    id: string;
    jobType: string;
    status: string;
}

/**
 * Re-enqueues the import job for a failed migration without requiring the user to
 * re-run export/transform or re-enter setup. Calls POST /api/migrations/{id}/start-import,
 * which re-uses the already-exported and transformed artifacts. The import is idempotent —
 * import-block adoption re-adopts any resources already created in the target org.
 */
const retryImport = async (bearerToken: string, migrationId: string): Promise<RetryImportResult> => {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${migrationId}/start-import`;

    try {
        const response = await authenticatedFetch(url, {
            method: 'POST',
            headers: {
                ...getV2AuthHeaders(bearerToken),
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            logger.error('Retry import API returned error', {
                status: response.status,
                statusText: response.statusText,
                migrationId,
                url
            });
            throw new Error(`Error retrying import: ${response.status} ${response.statusText}`);
        }

        const data: RetryImportResult = await response.json();
        logger.info('Import retry queued successfully', {
            migrationId,
            jobType: data.jobType,
            status: data.status
        });
        return data;
    } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        logger.error('Failed to retry import', { error: msg, migrationId, url });
        throw error;
    }
};

export default retryImport;
